import styled from 'styled-components';
import { useLanguage } from '../../../contexts/LanguageContext';

const PREDEFINED_TAGS = {
  room: [
    { id: 'bestseller', en: '🏆 Best Seller', vi: '🏆 Bán chạy nhất', color: '#e74c3c' },
    { id: 'popular', en: '⭐ Popular', vi: '⭐ Phổ biến', color: '#f39c12' },
    { id: 'new', en: '✨ New', vi: '✨ Mới', color: '#27ae60' },
    { id: 'featured', en: '🌟 Featured', vi: '🌟 Nổi bật', color: '#8e44ad' },
    { id: 'luxury', en: '💎 Luxury', vi: '💎 Sang trọng', color: '#2c3e50' },
    { id: 'budget', en: '💰 Budget Friendly', vi: '💰 Tiết kiệm', color: '#16a085' },
    { id: 'family', en: '👨‍👩‍👧‍👦 Family', vi: '👨‍👩‍👧‍👦 Gia đình', color: '#e67e22' },
    { id: 'romantic', en: '💕 Romantic', vi: '💕 Lãng mạn', color: '#e91e63' },
  ],
  activity: [
    { id: 'bestseller', en: '🏆 Best Seller', vi: '🏆 Bán chạy nhất', color: '#e74c3c' },
    { id: 'popular', en: '⭐ Popular', vi: '⭐ Phổ biến', color: '#f39c12' },
    { id: 'new', en: '✨ New', vi: '✨ Mới', color: '#27ae60' },
    { id: 'featured', en: '🌟 Featured', vi: '🌟 Nổi bật', color: '#8e44ad' },
    { id: 'adventure', en: '🎯 Adventure', vi: '🎯 Phiêu lưu', color: '#795548' },
    { id: 'relaxing', en: '🧘 Relaxing', vi: '🧘 Thư giãn', color: '#607d8b' },
    { id: 'family', en: '👨‍👩‍👧‍👦 Family Friendly', vi: '👨‍👩‍👧‍👦 Phù hợp gia đình', color: '#e67e22' },
    { id: 'cultural', en: '🏛️ Cultural', vi: '🏛️ Văn hóa', color: '#9c27b0' },
  ]
};

const InputGroup = styled.div`
  margin-bottom: 1rem;
`;

const InputLabel = styled.label`
  display: block;
  margin-bottom: 0.5rem;
  font-weight: 600;
  color: var(--text-primary);
`;

const TagContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const TagOption = styled.button`
  display: flex;
  align-items: center;
  gap: 0.3rem;
  padding: 0.5rem 0.8rem;
  border: 2px solid ${props => props.selected ? '#3498db' : '#ddd'};
  background: ${props => props.selected ? '#3498db' : 'var(--bg-secondary)'};
  color: ${props => props.selected ? 'white' : 'var(--text-primary)'};
  border-radius: 20px;
  cursor: pointer;
  font-size: 0.9rem;
  font-weight: ${props => props.selected ? 'bold' : 'normal'};
  transition: all 0.3s;

  &:hover {
    border-color: #3498db;
    background: ${props => props.selected ? '#2980b9' : '#e3f2fd'};
  }

  &:focus {
    outline: none;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.3);
  }
`;

const Tag = styled.span`
  display: inline-block;
  padding: 0.3rem 0.6rem;
  margin: 0.2rem;
  border-radius: 15px;
  font-size: 0.8rem;
  font-weight: bold;
  color: white;
  background: ${props => {
    switch(props.type) {
      case 'bestseller': return '#e74c3c';
      case 'popular': return '#f39c12';
      case 'new': return '#27ae60';
      case 'featured': return '#8e44ad';
      case 'luxury': return '#2c3e50';
      case 'budget': return '#16a085';
      case 'family': return '#e67e22';
      case 'romantic': return '#e91e63';
      case 'adventure': return '#795548';
      case 'relaxing': return '#607d8b';
      case 'cultural': return '#9c27b0';
      default: return '#95a5a6';
    }
  }};
`;

const TagSelector = ({ type, selectedTags = [], onChange, label }) => {
  const { currentLanguage } = useLanguage();
  const tags = PREDEFINED_TAGS[type] || [];

  const handleTagChange = (tagId, event) => {
    event.preventDefault();
    event.stopPropagation();
    
    const newTags = selectedTags.includes(tagId) 
      ? selectedTags.filter(id => id !== tagId)
      : [...selectedTags, tagId];
    onChange(newTags);
  };

  return (
    <InputGroup>
      <InputLabel>{label}</InputLabel>
      <TagContainer>
        {tags.map(tag => {
          const isSelected = selectedTags.includes(tag.id);
          return (
            <TagOption 
              key={tag.id} 
              selected={isSelected}
              onClick={(e) => handleTagChange(tag.id, e)}
              type="button"
            >
              {currentLanguage === 'vi' ? tag.vi : tag.en}
            </TagOption>
          );
        })}
      </TagContainer>
    </InputGroup>
  );
};

const TagDisplay = ({ tags = [], type }) => {
  const { currentLanguage } = useLanguage();
  const availableTags = PREDEFINED_TAGS[type] || [];
  
  if (!tags || tags.length === 0) return null;

  return (
    <div style={{ margin: '0.5rem 0' }}>
      {tags.map(tagId => {
        const tagInfo = availableTags.find(t => t.id === tagId);
        if (!tagInfo) return null;
        
        return (
          <Tag key={tagId} type={tagId}>
            {currentLanguage === 'vi' ? tagInfo.vi : tagInfo.en}
          </Tag>
        );
      })}
    </div>
  );
};

export { TagSelector, TagDisplay, PREDEFINED_TAGS };
export default TagSelector;